import {
    ChangeDetectionStrategy,
    Component,
    computed,
    input,
} from '@angular/core';
import { NgIcon } from '@ng-icons/core';
import { injectSupportsHoverInteraction } from '@quipex/core/utils';
import {
    injectPopoverWithHover,
    QpxButton,
    QpxIcon,
    QpxPill,
    QpxTypography,
} from '@quipex/shared/ui';
import type { BuildingIdentityFactPopover } from './building-identity-fact-popover/building-identity-fact-popover';

type IdentityFactSource = 'user' | 'geoscape';
type IdentityFactPopoverKind = 'definition' | 'source';

@Component({
    selector: 'qpx-building-identity-fact',
    imports: [NgIcon, QpxButton, QpxIcon, QpxPill, QpxTypography],
    templateUrl: './building-identity-fact.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'identity-field inline-flex max-w-full min-w-0 items-center gap-1',
        '(mouseover)': 'onMouseOver($event)',
        '(mouseout)': 'onMouseOut($event)',
    },
})
export class BuildingIdentityFact {
    public readonly definitionTriggerLabel = input<string>();
    public readonly label = input.required<string>();
    public readonly value = input.required<string>();
    public readonly muted = input(true);
    public readonly icon = input.required<string>();
    public readonly iconOnly = input(false);
    public readonly showDefinition = input(true);
    public readonly showSource = input(true);
    public readonly iconSize = input<'xs' | 'sm'>('sm');
    public readonly definition = input.required<string>();
    public readonly definitionCitationLabel = input<string>();
    public readonly definitionCitationUrl = input<string>();
    public readonly source = input.required<IdentityFactSource>();
    public readonly geoscapeLabel = input('Geoscape');
    public readonly sourceDescription = input<string>();
    public readonly sourceCitationUrl = input<string>();

    protected readonly sourceLabel = computed(() =>
        this.source() === 'geoscape' ? this.geoscapeLabel() : 'User supplied'
    );
    protected readonly sourceIcon = computed(() =>
        this.source() === 'geoscape' ? 'lucideGlobe' : 'lucideUser'
    );
    protected readonly sourceContext = computed(() =>
        this.source() === 'geoscape' ? 'primary' : 'caution'
    );

    private readonly supportsHover = injectSupportsHoverInteraction();
    private readonly triggers: Partial<
        Record<IdentityFactPopoverKind, HTMLButtonElement>
    > = {};
    private activated: IdentityFactPopoverKind | null = null;
    protected readonly definitionPopover = this.createPopover('definition');
    protected readonly sourcePopover = this.createPopover('source');

    protected onMouseOver(event: MouseEvent): void {
        if (!this.supportsHover()) return;
        const trigger = this.findTrigger(event.target);
        if (!trigger || trigger.contains(event.relatedTarget as Node)) return;
        const kind = this.kindOf(trigger);
        this.triggers[kind] = trigger;
        this.popover(kind).enterTrigger(trigger);
    }

    protected onMouseOut(event: MouseEvent): void {
        if (!this.supportsHover()) return;
        const trigger = this.findTrigger(event.target);
        if (!trigger || trigger.contains(event.relatedTarget as Node)) return;
        const kind = this.kindOf(trigger);
        if (this.activated !== kind)
            this.popover(kind).leaveTrigger(event.relatedTarget);
    }

    protected onClick(kind: IdentityFactPopoverKind, event: Event): void {
        const trigger = event.currentTarget;
        if (!(trigger instanceof HTMLButtonElement)) return;
        this.triggers[kind] = trigger;
        if (this.popover(kind).isOpen()) {
            if (this.activated === kind) {
                this.popover(kind).toggle(trigger);
                return;
            }
            this.popover(kind).close();
        }
        this.activated = kind;
        this.popover(kind).toggle(trigger);
    }

    protected onFocusOut(
        kind: IdentityFactPopoverKind,
        event: FocusEvent
    ): void {
        if (this.activated === kind) this.activated = null;
        this.popover(kind).leaveTrigger(event.relatedTarget);
    }

    private createPopover(kind: IdentityFactPopoverKind) {
        return injectPopoverWithHover<BuildingIdentityFactPopover>({
            componentInputs: () => ({
                kind,
                label: this.label(),
                value: this.iconOnly() ? this.value() : undefined,
                includeSource:
                    this.iconOnly() &&
                    Boolean(this.value()) &&
                    this.showSource(),
                definition: this.definition(),
                citationLabel: this.definitionCitationLabel(),
                citationUrl: this.definitionCitationUrl(),
                source: this.source(),
                sourceDescription: this.sourceDescription(),
                sourceCitationUrl: this.sourceCitationUrl(),
                getTrigger: () => this.triggers[kind] ?? null,
                shouldFocusLink: () => this.activated === kind,
            }),
            loadComponent: async () =>
                (
                    await import('./building-identity-fact-popover/building-identity-fact-popover')
                ).BuildingIdentityFactPopover,
            triggerSelector: `[data-building-identity-${kind}-trigger]`,
            overlaySelector: `[data-building-identity-${kind}-popover]`,
            onResult: () => {
                if (this.activated === kind) this.activated = null;
            },
        });
    }

    private popover(kind: IdentityFactPopoverKind) {
        return kind === 'definition'
            ? this.definitionPopover
            : this.sourcePopover;
    }

    private findTrigger(target: EventTarget | null): HTMLButtonElement | null {
        if (!(target instanceof Element)) return null;
        return target.closest<HTMLButtonElement>(
            '[data-building-identity-definition-trigger], [data-building-identity-source-trigger]'
        );
    }

    private kindOf(trigger: HTMLButtonElement): IdentityFactPopoverKind {
        return trigger.dataset['buildingIdentityDefinitionTrigger'] !==
            undefined
            ? 'definition'
            : 'source';
    }
}
